import "mapbox-gl/dist/mapbox-gl.css"
import type { MapboxGeoJSONFeature } from "mapbox-gl"
import { Marker as MapboxMarker } from "mapbox-gl"
import type { MapRef } from "react-map-gl/mapbox"
import { useState, useRef, useCallback, useEffect, useMemo } from "react"
import { Head, usePage } from "@inertiajs/react"
import { toast } from "sonner"

import { PageHeader } from "@/hooks/use-page-header"
import { CommuneContext } from "@/components/explore/explore-context"
import { ExploreMap } from "@/components/explore/explore-map"
import { Google3DMap } from "@/components/explore/google-3d-map"
import { StreetViewMode } from "@/components/explore/street-view-mode"
import { ParcelCard } from "@/components/explore/panels/parcel-card"
import { AddressSearchBar } from "@/components/explore/panels/address-search-bar"
import { MapStyleSelector } from "@/components/explore/panels/map-style-selector"
import { ToolsBar } from "@/components/explore/panels/tools-bar"
import { LayersPanel } from "@/components/explore/panels/layers-panel"
import { LayerLegends } from "@/components/explore/panels/layer-legends"
import { MapLoadingIndicator } from "@/components/explore/panels/map-loading-indicator"
import { KeyboardShortcutsModal } from "@/components/explore/panels/keyboard-shortcuts-modal"
import { CreateFolderDialog } from "@/components/explore/panels/create-folder-dialog"
import { OnboardingTour } from "@/components/explore/onboarding/onboarding-tour"

import { useMapClickHandler } from "@/hooks/explore/use-map-click-handler"
import { useCommuneSelection } from "@/hooks/explore/use-commune-selection"
import { useInitialLoad } from "@/hooks/explore/use-initial-load"
import { useLayerManager } from "@/hooks/explore/use-layer-manager"
import { useKeyboardShortcuts } from "@/hooks/explore/use-keyboard-shortcuts"
import { useParcelSearch } from "@/hooks/explore/use-parcel-search"
import { useConstructionStudy } from "@/hooks/explore/use-construction-study"
import { useAutoCommune } from "@/hooks/explore/use-auto-commune"
import { usePLUData } from "@/hooks/explore/use-plu-data"
import { useParcelSelection } from "@/hooks/explore/use-parcel-selection"
import { useMapLoading } from "@/hooks/explore/use-map-loading"
import { useMapMarker } from "@/hooks/explore/use-map-markers"

import { getTerrainElevation } from "@/lib/explore/api/geo"
import { getKaptcherParcelle } from "@/lib/explore/api/kaptcher-data"
import type { IParcel, IPermit, IDPE, POIMarker, MutationMarker } from "@/types/explore"
import type { ProjectPopupData } from "@/components/explore/panels/project-popup"

import { AppLayout } from "@/layouts/app-layout"

import "@/components/explore/explorer-v2.css"

function ExplorePage() {
  const { mapbox_token, google_maps_key, communes, pipe_statuses } = usePage<{
    mapbox_token: string
    google_maps_key: string
    communes?: { code_com: string; code_dep: string; nom: string; latitude: number; longitude: number }[]
    pipe_statuses: import("@/types").PipeStatusRecord[]
  }>().props

  // Read URL query params for deep-linking
  const urlParams = useMemo(() => {
    const sp = new URLSearchParams(window.location.search)
    const commune = sp.get("commune") || undefined
    const lat = sp.get("lat") ? parseFloat(sp.get("lat")!) : undefined
    const lng = sp.get("lng") ? parseFloat(sp.get("lng")!) : undefined
    const center = lat && lng ? ([lng, lat] as [number, number]) : undefined
    return { commune, center }
  }, [])

  // Map state
  const [mapCenter] = useState<[number, number]>([2.4973143200662022, 48.798628021394556])
  const [style, setStyle] = useState("street")
  const mapRef = useRef<MapRef>(null)

  // Selection state
  const [selectedParcels, setSelectedParcels] = useState<IParcel[]>([])
  const [selectedPC, setSelectedPC] = useState<{ permit: IPermit; longitude: number; latitude: number } | null>(null)
  const [selectedDPE, setSelectedDPE] = useState<{ dpe: IDPE; longitude: number; latitude: number } | null>(null)
  const [selectedPLU, setSelectedPLU] = useState<MapboxGeoJSONFeature | null>(null)
  const [parcelPOI, setParcelPOI] = useState<{ lon: number | null; lat: number | null }>({ lon: null, lat: null })
  const [selectedProject, setSelectedProject] = useState<{
    project: ProjectPopupData
    longitude: number
    latitude: number
  } | null>(null)
  const [prescriptionPopup, setPrescriptionPopup] = useState<{
    longitude: number
    latitude: number
    libelle: string
    txt: string
  } | null>(null)

  // Mode state
  const [isStudyMode, setIsStudyMode] = useState(false)
  const [isToolOn, setIsToolOn] = useState(false)
  const [activeTool, setActiveTool] = useState<string | null>(null)
  const [isParcelFilterOn, setIsParcelFilterOn] = useState(false)
  const [parcelFilter, setParcelFilter] = useState<{
    minParcelSurface: number
    maxParcelSurface: number
    ownerTypes: string[]
    siren: string
  }>({
    minParcelSurface: 0,
    maxParcelSurface: 0,
    ownerTypes: ["Prive", "Public"],
    siren: "",
  })
  const [parcelFilterResults, setParcelFilterResults] = useState<IParcel[]>([])
  const [activePOIMarkers, setActivePOIMarkers] = useState<POIMarker[]>([])
  const [activeMutationMarkers, setActiveMutationMarkers] = useState<MutationMarker[]>([])
  const poiHoverMarkerRef = useRef<MapboxMarker | null>(null)
  const [createFolder, setCreateFolder] = useState(false)
  const [elevation, setElevation] = useState<number | null>(null)

  // Save selected parcel to recent list
  useEffect(() => {
    const parcel = selectedParcels[0]
    if (parcel) parcelSearch.addRecentParcel(parcel)
  }, [selectedParcels])

  // Enrich selected parcel with detail data (corporationOwners, etc.)
  useEffect(() => {
    const parcel = selectedParcels[0]
    const idu = parcel?.properties?.idu || parcel?.id
    if (!parcel || !idu || Array.isArray(parcel.corporationOwners)) return

    let cancelled = false
    getKaptcherParcelle(idu)
      .then((detail) => {
        if (cancelled || !detail) return
        setSelectedParcels((prev) => prev.map((p) => ((p.properties?.idu || p.id) === idu ? { ...p, ...detail } : p)))
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [selectedParcels])

  // Fetch elevation when selected parcel changes
  useEffect(() => {
    const parcel = selectedParcels[0]
    const center = parcel?.center
    if (!center) {
      setElevation(null)
      return
    }
    let cancelled = false
    setElevation(null)
    getTerrainElevation(center[0], center[1]).then((elev) => {
      if (!cancelled) setElevation(elev)
    })
    return () => {
      cancelled = true
    }
  }, [selectedParcels])

  // UI state
  const [layersOpen, setLayersOpen] = useState(false)
  const [shortcutsOpen, setShortcutsOpen] = useState(false)
  const [addressSearch, setAddressSearch] = useState("")

  // Loading
  const { isLoading, displayMessage, setLoading, onMoveStart, onMapIdle } = useMapLoading()

  // Commune selection
  const { onSelectCommune, selectedCommune, setSelectedCommune, showCommuneWarning, setShowCommuneWarning } =
    useCommuneSelection({ selectedParcels, communes })

  // Auto-commune detection
  const { wasAutoSelectedRef, debouncedHandleMapMoveEnd, handleManualSelectCommune } = useAutoCommune({
    mapRef,
    selectedCommune,
    onSelectCommune,
    setSelectedCommune,
  })

  useInitialLoad({
    code_insee: urlParams.commune,
    center: urlParams.center,
    onSelectCommune,
    mapRef,
    setSelectedParcels,
    setParcelPOI,
  })

  // Layer manager
  const layerManager = useLayerManager()

  // Parcel search
  const parcelSearch = useParcelSearch({
    communeCode: selectedCommune?.code,
    mapRef,
  })

  // PLU data
  const { pluData, pluTypeZones, pluOpacity, pluSubzoneOptions, pluLibelles } = usePLUData(
    selectedCommune,
    layerManager.layerFilters,
  )

  const dynamicOptions = useMemo(() => ({ "plu-subzones": pluSubzoneOptions }), [pluSubzoneOptions])

  // Construction study
  const study = useConstructionStudy({
    parcels: selectedParcels,
    selectedPLU,
    selectedCommune,
  })

  // Map click handler
  const { handleClick, onMouseHandler, onMouseLeave } = useMapClickHandler({
    selectedParcels,
    isToolOn,
    isStudyMode,
    isParcelFilterOn,
    setSelectedParcels,
    setSelectedPC,
    setSelectedPLU,
    setPrescriptionPopup,
    setParcelPOI,
    setLoading,
  })

  // Keyboard shortcuts
  useKeyboardShortcuts({
    quickLayers: layerManager.quickLayers,
    toggleLayer: layerManager.toggleLayer,
    setLayersOpen,
    setShortcutsOpen,
    onEscape: () => {
      setSelectedParcels([])
      setParcelPOI({ lon: null, lat: null })
      setSelectedDPE(null)
      setLayersOpen(false)
      setShortcutsOpen(false)
    },
  })

  // Show commune warning as toast
  useEffect(() => {
    if (showCommuneWarning) {
      toast.warning("La commune a change suite a votre deplacement sur la carte.")
      setShowCommuneWarning(false)
    }
  }, [showCommuneWarning, setShowCommuneWarning])

  // Fly to commune when it changes
  useEffect(() => {
    if (wasAutoSelectedRef.current) {
      wasAutoSelectedRef.current = false
      return
    }
    if (selectedCommune?.centre?.coordinates && mapRef.current) {
      const [lng, lat] = selectedCommune.centre.coordinates
      mapRef.current.flyTo({ center: [lng, lat], zoom: 13, duration: 1500 })
    }
  }, [selectedCommune?.code])

  // Hover markers
  const addressMarker = useMapMarker(mapRef, "#1E40AF")
  const parcelHoverMarker = useMapMarker(mapRef, "#059669")
  const hoverMarker = useMapMarker(mapRef, "#1E40AF")

  // Parcel selection handlers
  const { handleSelectRecent, handleSelectParcelResult, handleSelectFilterResult } = useParcelSelection({
    mapRef,
    setSelectedParcels,
    setLoading,
    parcelHoverMarker,
    clearSearch: parcelSearch.clearSearch,
  })

  const handleAddressSelect = useCallback(
    (result: { center: [number, number]; label: string }) => {
      addressMarker.onHover(result.center)
      mapRef.current?.flyTo({ center: result.center, zoom: 17, duration: 1500 })
    },
    [addressMarker],
  )

  const handleHoverPOI = useCallback((poi: POIMarker | null) => {
    poiHoverMarkerRef.current?.remove()
    poiHoverMarkerRef.current = null
    if (!poi) return
    const map = mapRef.current?.getMap()
    if (!map) return
    const COLORS: Record<string, string> = {
      Transport: "#3b82f6",
      Commerce: "#f97316",
      Sante: "#ef4444",
      Education: "#22c55e",
      Monuments: "#8b5cf6",
      Autres: "#6b7280",
    }
    poiHoverMarkerRef.current = new MapboxMarker({ color: COLORS[poi.category] || COLORS.Autres })
      .setLngLat([poi.lon, poi.lat])
      .addTo(map)
  }, [])

  const handleStyleChange = (newStyle: string) => {
    if (["street", "mono", "satellite", "cadastre"].includes(newStyle)) {
      setLoading("style", true)
      setStyle(newStyle)
    } else if (newStyle === "3d" || newStyle === "streetview") {
      setStyle(newStyle)
    }
  }

  const handleFlyTo = useCallback(
    (c: [number, number], zoom?: number) => mapRef.current?.flyTo({ center: c, zoom: zoom ?? 18, duration: 1500 }),
    [],
  )

  const handlePoiFlyTo = useCallback(
    (c: [number, number]) => mapRef.current?.flyTo({ center: c, zoom: 16, duration: 1500 }),
    [],
  )

  const handleParcelFilterDataLoaded = useCallback((features: IParcel[]) => {
    setParcelFilterResults(features)
  }, [])

  return (
    <>
      <Head title="Explorateur" />
      <PageHeader title="Explorateur" />
      <div id="explore-map-v2-root" className="explorer-v2 w-full h-full relative">
        <CommuneContext.Provider value={selectedCommune}>
          <CreateFolderDialog
            open={createFolder}
            onClose={() => setCreateFolder(false)}
            selectedParcels={selectedParcels}
            commune={selectedCommune}
            pipeStatuses={pipe_statuses}
          />

          {style === "3d" ? (
            <Google3DMap
              longitude={selectedCommune?.centre?.coordinates?.[0] ?? mapCenter[0]}
              latitude={selectedCommune?.centre?.coordinates?.[1] ?? mapCenter[1]}
              googleMapsKey={google_maps_key}
              selectedParcels={selectedParcels}
            />
          ) : style === "streetview" ? (
            <StreetViewMode
              mapboxToken={mapbox_token}
              longitude={selectedCommune?.centre?.coordinates?.[0] ?? mapCenter[0]}
              latitude={selectedCommune?.centre?.coordinates?.[1] ?? mapCenter[1]}
              googleMapsKey={google_maps_key}
            />
          ) : (
            <ExploreMap
              mapRef={mapRef}
              mapboxToken={mapbox_token}
              style={style}
              selectedCommune={selectedCommune}
              selectedParcels={selectedParcels}
              selectedPLU={selectedPLU}
              isStudyMode={isStudyMode}
              isToolOn={isToolOn}
              activeTool={activeTool}
              activeLayers={layerManager.activeLayers}
              layerFilters={layerManager.layerFilters}
              pluData={pluData}
              pluTypeZones={pluTypeZones}
              pluOpacity={pluOpacity}
              pluLibelles={pluLibelles}
              parcelPOI={parcelPOI}
              selectedPC={selectedPC}
              setSelectedPC={setSelectedPC}
              selectedDPE={selectedDPE}
              setSelectedDPE={setSelectedDPE}
              selectedProject={selectedProject}
              setSelectedProject={setSelectedProject}
              prescriptionPopup={prescriptionPopup}
              setPrescriptionPopup={setPrescriptionPopup}
              handleClick={handleClick}
              onMouseHandler={onMouseHandler}
              onMouseLeave={onMouseLeave}
              center={mapCenter}
              onMapIdle={onMapIdle}
              onMoveStart={onMoveStart}
              onMoveEnd={debouncedHandleMapMoveEnd}
              isCommuneCovered={true}
              parcelFilter={parcelFilter}
              isParcelFilterOn={isParcelFilterOn}
              onParcelFilterDataLoaded={handleParcelFilterDataLoaded}
              activePOIMarkers={activePOIMarkers}
              activeMutationMarkers={activeMutationMarkers}
              study={isStudyMode && selectedParcels.length > 0 ? study : undefined}
            />
          )}

          <div className={style === "streetview" ? "absolute top-0 left-0 w-1/2 z-10" : ""}>
            <MapStyleSelector activeStyle={style} onChange={handleStyleChange} compact={style === "streetview"} />
          </div>

          {style !== "3d" && style !== "streetview" && (
            <>
              <ParcelCard
                selectedParcels={selectedParcels}
                selectedPLU={selectedPLU}
                setSelectedParcels={setSelectedParcels}
                setParcelPOI={setParcelPOI}
                searchValue={parcelSearch.searchValue}
                onSearchChange={parcelSearch.onSearchValueChange}
                recentParcels={parcelSearch.recentParcels}
                onSelectRecent={handleSelectRecent}
                onCreateFolder={() => setCreateFolder(true)}
                searchResults={parcelSearch.searchResults}
                isSearching={parcelSearch.isSearching}
                onSelectSearchResult={handleSelectParcelResult}
                onClearSearch={parcelSearch.clearSearch}
                filtersEnabled={isParcelFilterOn}
                setFiltersEnabled={setIsParcelFilterOn}
                onFilterChange={setParcelFilter}
                parcelFilterResults={parcelFilterResults}
                onSelectFilterResult={handleSelectFilterResult}
                onHoverResult={parcelHoverMarker.onHover}
                onLeaveResult={parcelHoverMarker.onLeave}
                addRecentParcel={parcelSearch.addRecentParcel}
                removeRecentParcel={parcelSearch.removeRecentParcel}
                onFlyTo={handleFlyTo}
                onPoiFlyTo={handlePoiFlyTo}
                onActivePOIsChange={setActivePOIMarkers}
                onActiveMutationsChange={setActiveMutationMarkers}
                onHoverPOI={handleHoverPOI}
                isToolActive={!!activeTool}
                elevation={elevation}
              />

              <AddressSearchBar
                onSelectCommune={handleManualSelectCommune}
                communes={communes}
                selectedCommune={selectedCommune}
                addressSearch={addressSearch}
                setAddressSearch={setAddressSearch}
                onAddressSelect={handleAddressSelect}
                setLoading={setLoading}
                mapRef={mapRef}
                onHoverResult={hoverMarker.onHover}
                onLeaveResult={hoverMarker.onLeave}
                onClearAddress={addressMarker.remove}
                isCommuneCovered={true}
              />

              <MapLoadingIndicator isLoading={isLoading} message={displayMessage} />

              <ToolsBar
                activeTool={activeTool}
                setActiveTool={setActiveTool}
                isStudyMode={isStudyMode}
                setIsStudyMode={setIsStudyMode}
                setIsToolOn={setIsToolOn}
                onShortcutsOpen={() => setShortcutsOpen(true)}
                selectedParcelsCount={selectedParcels.length}
                study={isStudyMode ? study : undefined}
                mapRef={mapRef}
              />

              <LayersPanel
                layersOpen={layersOpen}
                setLayersOpen={setLayersOpen}
                activeLayers={layerManager.activeLayers}
                setActiveLayers={layerManager.setActiveLayers}
                toggleLayer={layerManager.toggleLayer}
                quickLayers={layerManager.quickLayers}
                quickLayerItems={layerManager.quickLayerItems}
                toggleQuickLayer={layerManager.toggleQuickLayer}
                activeCount={layerManager.activeCount}
                filteredLayerGroups={layerManager.filteredLayerGroups}
                layerSearch={layerManager.layerSearch}
                setLayerSearch={layerManager.setLayerSearch}
                layerPresets={layerManager.layerPresets}
                savePreset={layerManager.savePreset}
                loadPreset={layerManager.loadPreset}
                deletePreset={layerManager.deletePreset}
                setLoading={setLoading}
                isToolActive={!!activeTool}
                onFilterChange={layerManager.setLayerFilter}
                dynamicOptions={dynamicOptions}
              />

              <LayerLegends activeLayerItems={layerManager.activeLayerItems} />

              <KeyboardShortcutsModal
                open={shortcutsOpen}
                onClose={() => setShortcutsOpen(false)}
                quickLayers={layerManager.quickLayers}
              />

              <OnboardingTour
                setLayersOpen={setLayersOpen}
                setSelectedParcels={setSelectedParcels}
                setParcelPOI={setParcelPOI}
                selectedParcels={selectedParcels}
              />
            </>
          )}
        </CommuneContext.Provider>
      </div>
    </>
  )
}

ExplorePage.layout = (page: React.ReactNode) => <AppLayout noPadding>{page}</AppLayout>

export default ExplorePage
